import React from "react";
import { motion } from "framer-motion";
import { Search, ClipboardList, HandHeart, Rocket } from "lucide-react";

const pillars = [
  {
    icon: Search,
    title: "Discoverable",
    body: "Increase visibility among organizations, procurement teams, and partners actively looking for women-led suppliers.",
    tone: "bg-[#F9E8ED]",
  },
  {
    icon: ClipboardList,
    title: "Procurement-Ready",
    body: "Present your business in a structured, professional format that helps organizations evaluate and engage with confidence.",
    tone: "bg-[#F5EBD9]",
  },
  {
    icon: HandHeart,
    title: "Business-Ready",
    body: "Build trust through a curated profile that showcases your capabilities, products, services, and credentials.",
    tone: "bg-[#E6EFE1]",
  },
  {
    icon: Rocket,
    title: "Connected to Opportunities",
    body: "Access procurement opportunities, partnerships, collaborations, events, and initiatives across the Yuukke ecosystem.",
    tone: "bg-[#EAE4F1]",
  },
];

const stagger = {
  hidden: {},
  show: { transition: { staggerChildren: 0.08 } },
};
const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: { opacity: 1, y: 0, transition: { type: "spring", damping: 24, stiffness: 100 } },
};

export default function Pillars() {
  return (
    <section id="why" className="py-24 md:py-32 bg-brand-cream relative" data-testid="pillars-section">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          className="max-w-3xl"
        >
          <span className="text-xs uppercase tracking-[0.25em] font-semibold text-brand-wine">
            Why Yuukke Business Exchange
          </span>
          <h2 className="mt-4 font-serif text-4xl md:text-5xl lg:text-6xl font-medium tracking-tight leading-[1.1] text-brand-ink">
            Building businesses <span className="italic text-brand-wine">ready for opportunity</span>.
          </h2>
          <p className="mt-6 text-lg text-brand-muted max-w-2xl leading-relaxed">
            At Yuukke we believe great businesses deserve to be discovered — and buyers deserve access to trusted, procurement-ready suppliers.
          </p>
        </motion.div>

        <motion.div
          variants={stagger}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-80px" }}
          className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8"
        >
          {pillars.map((p) => {
            const Icon = p.icon;
            return (
              <motion.div
                variants={fadeUp}
                key={p.title}
                className="bg-white rounded-2xl p-8 border border-brand-line hover:border-brand-wine/40 transition-all duration-500 hover:-translate-y-1 hover:shadow-[0_20px_40px_-25px_rgba(139,30,63,0.35)]"
                data-testid={`pillar-${p.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              >
                <div className={`w-14 h-14 rounded-full flex items-center justify-center ${p.tone} mb-6`}>
                  <Icon size={22} className="text-brand-wine" strokeWidth={1.6} />
                </div>
                <h3 className="font-serif text-2xl font-semibold text-brand-ink leading-snug">
                  {p.title}
                </h3>
                <p className="mt-3 text-[15px] text-brand-muted leading-relaxed">{p.body}</p>
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
}
